<template>
  <v-container class="py-md-8">
    <h2 class="text-h2 flex-fill px-3">Access Keys</h2>

    <v-breadcrumbs
      class="px-3 mb-4 pt-3"
      :items="breadcrumbs"
      divider=">"
    ></v-breadcrumbs>

    <v-alert type="info" color="secondary" elevation="1" :value="archived"
      >This survey has been archived. Options are limited.</v-alert
    >

    <v-data-table
      v-if="showCounts"
      :headers="headers"
      :items="counts"
      item-key="name"
      class="elevation-1"
      :search="search"
      :loading="loading"
      @click:row="showBatch"
    >
      <template v-slot:top>
        <div>
          <div class="d-flex align-center pa-4">
            <v-text-field
              rounded
              flat
              filled
              type="search"
              v-model="search"
              dense
              hide-details=""
              placeholder="Search"
              class="mr-2 flex-fill"
              :disabled="loading"
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>

            <v-menu offset-y left :disabled="loading">
              <template v-slot:activator="menu">
                <v-tooltip top>
                  <template v-slot:activator="tooltip">
                    <div v-bind="tooltip.attrs" v-on="tooltip.ttOn">
                      <v-btn
                        v-bind="menu.attrs"
                        v-on="menu.on"
                        icon
                        :disabled="loading"
                      >
                        <v-icon>$more</v-icon>
                      </v-btn>
                    </div>
                  </template>
                  <span>Access Key Options</span>
                </v-tooltip>
              </template>

              <v-list>
                <v-list-item link @click="openAccessKeySettings = true">
                  <v-list-item-content>
                    <div class="text-nowrap d-flex align-center">
                      <v-icon class="mr-4">$settings</v-icon>
                      Access Key Settings
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  link
                  @click="openGenerateKeys = true"
                  :disabled="archived"
                >
                  <v-list-item-content>
                    <div class="text-nowrap d-flex align-center">
                      <v-icon class="mr-4">$key</v-icon>
                      Generate Keys
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  link
                  @click="openGenerateBatches = true"
                  :disabled="archived"
                >
                  <v-list-item-content>
                    <div class="text-nowrap d-flex align-center">
                      <v-icon class="mr-4">$upload</v-icon>
                      Generate Keys from File
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item link @click="() => downloadCodes()">
                  <v-list-item-content>
                    <div class="text-nowrap d-flex align-center">
                      <v-icon class="mr-4">$download</v-icon>
                      Download Access Keys
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item link @click="() => downloadCounts()">
                  <v-list-item-content>
                    <div class="text-nowrap d-flex align-center">
                      <v-icon class="mr-4">$download</v-icon>
                      Download Meta Data Counts
                    </div>
                  </v-list-item-content>
                </v-list-item>

                <!-- <v-list-item link @click="() => downloadResponses()">
                <v-list-item-content>
                  <div class="text-nowrap d-flex align-center">
                    <v-icon class="mr-4">$download</v-icon>
                    Download Responses
                  </div>
                </v-list-item-content>
              </v-list-item> -->

                <v-list-item
                  link
                  @click="openConfirmDeleteAll = true"
                  :disabled="archived"
                >
                  <v-list-item-content>
                    <div class="text-nowrap d-flex align-center">
                      <v-icon class="mr-4">$delete</v-icon>
                      Delete All Access Keys
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <v-alert type="info" color="primary" rounded="0" class="mb-0">
            {{ codeStats.rate }} of code have been used. ({{
              codeStats.used | number
            }}/{{ codeStats.total | number }})
          </v-alert>
        </div>
      </template>

      <template v-slot:[`item.created`]="{ item }">
        {{ item.created | dateTime }}ttt
      </template> 

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu left offset-y>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  icon
                  class="mr-2"
                  v-bind="attrs"
                  @click.stop
                  v-on="{ ...tooltip, ...menu }"
                >
                  <v-icon small>$download</v-icon>
                </v-btn>
              </template>
              <span>Download</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item link @click.stop="() => downloadCodes(item.metaData)">
              <v-list-item-title>
                <v-icon class="mr-2">$key</v-icon> Access Keys
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              link
              @click.stop="() => downloadResponses(item.metaData)"
            >
              <v-list-item-title>
                <v-icon class="mr-2">$csv</v-icon> Responses
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              @click.stop="confirmDeleteBatch(item)"
              :disabled="loading || archived"
            >
              <v-icon small>$delete</v-icon>
            </v-btn>
          </template>
          <span>Delete</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-data-table
      v-else
      :headers="batchHeaders"
      :items="batchItems"
      class="elevation-1"
      :search="batchSearch"
      :loading="loading"
    >
      <template v-slot:top>
        <div>
          <div class="d-flex align-center pa-4">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  @click="showCounts = true"
                  class="mr-2"
                  text
                >
                  <v-icon class="mr-1">$prev</v-icon> Back
                </v-btn>
              </template>
              <span>Back</span>
            </v-tooltip>
            <v-text-field
              rounded
              flat
              filled
              type="search"
              v-model="batchSearch"
              dense
              hide-details=""
              placeholder="Search"
              class="mr-2 flex-fill"
              prepend-inner-icon="mdi-magnify"
              :disabled="loading"
            ></v-text-field>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  fab
                  color="primary"
                  small
                  elevation="0"
                  :disabled="loading || archived"
                  @click="openGenerateKeys = true"
                  ><font-awesome-icon icon="plus"></font-awesome-icon
                ></v-btn>
              </template>
              <span>Generate Access Keys</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  @click="downloadBatch"
                  :disabled="loading"
                >
                  <v-icon>$download</v-icon>
                </v-btn>
              </template>
              <span>Download Access Keys</span>
            </v-tooltip>
          </div>
          <div class="grey lighten-4">
            <v-divider></v-divider>
            <div class="d-flex px-4 py-3">
              <div class="mt-1">Meta Data:</div>
              <div>
                <v-chip
                  small
                  v-for="(metaData, i) in count.metaData"
                  :key="i"
                  class="ma-1"
                  color="primary"
                  >{{ metaData.key }}: {{ metaData.value }}</v-chip
                >
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:[`item.used`]="{ item }">
        <template v-if="item.used">
          <v-icon>$checked</v-icon> <span class="sr-only">Used</span>
        </template>
        <template v-else>
          <v-icon>$unchecked</v-icon> <span class="sr-only">Not Used</span>
        </template>
      </template>

      <template v-slot:[`item.active`]="{ item }">
        <v-tooltip right>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :color="item.active ? 'secondary' : ''"
              icon
              small
              v-on="on"
              v-bind="attrs"
              :disabled="loading || archived"
              @click="() => toggleActiveStatus(item)"
            >
              <template v-if="item.active">
                <v-icon>$checked</v-icon> <span class="sr-only">Active</span>
              </template>
              <template v-else>
                <v-icon>$unchecked</v-icon>
                <span class="sr-only">Inactive</span>
              </template>
            </v-btn>
          </template>
          <span>{{ item.active ? "Active" : "Inactive" }}</span>
        </v-tooltip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              @click.stop="showKey(item)"
              :disabled="loading || archived"
            >
              <v-icon small>$edit</v-icon>
            </v-btn>
          </template>
          <span>Edit</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              @click.stop="confirmDeleteCode(item)"
              :disabled="loading || archived"
            >
              <v-icon small>$delete</v-icon>
            </v-btn>
          </template>
          <span>Delete</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog
      v-model="openAccessKeySettings"
      max-width="600px"
      :persistent="loading"
    >
      <ValidationObserver
        ref="accessKeySettingsObserver"
        tag="form"
        @submit.prevent="onUpdateKeySettings"
      >
        <v-card>
          <dialog-title
            v-model="openAccessKeySettings"
            title="Access Key Settings"
            :disabled="loading"
          ></dialog-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                Use the form below to manage your survey's
                <strong>Access Key Settings</strong>.
              </v-col>
              <v-col cols="9">
                <ValidationProvider
                  rules="required|uniqueChar"
                  v-slot="{ errors }"
                  vid="Chars"
                >
                  <v-text-field
                    v-model="accessKeyChars"
                    label="Access Key Characters*"
                    hint="The characters that can appear in an access key."
                    persistent-hint
                    outlined
                    :error-messages="errors"
                    :disabled="loading || archived"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="3">
                <ValidationProvider
                  :rules="{
                    required: true,
                    numeric: true,
                    min_value: 1,
                    max_value: accessKeyChars ? accessKeyChars.length : 0,
                  }"
                  v-slot="{ errors }"
                  vid="Length"
                >
                  <v-text-field
                    v-model="survey.accessKeyLength"
                    label="Key Length*"
                    hint="The number of characters in each access key."
                    persistent-hint
                    outlined
                    type="number"
                    step="1"
                    :error-messages="errors"
                    :disabled="loading || archived"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  :value="possibleAccessKeys"
                  label="Number of access keys possible"
                  outlined
                  disabled
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  vid="AccessKeyUseType"
                >
                  <v-select
                    v-model="survey.accessKeyUseType"
                    label="Access Key Use Type*"
                    :items="accessKeyUseTypes"
                    persistent-hint
                    hint=""
                    outlined
                    :error-messages="errors"
                    :disabled="loading || archived"
                  ></v-select>
                </ValidationProvider>
              </v-col>

              <v-col cols="12" class="d-flex align-center">
                <small class="flex-fill">*indicates required field</small>
                <v-btn
                  :disabled="loading || archived"
                  text
                  color="primary"
                  @click="openAccessKeySettings = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  v-if="updatedKeySettings && !archived"
                  :loading="loading"
                  type="submit"
                  color="primary"
                >
                  Save
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <v-dialog
      v-model="openGenerateKeys"
      max-width="700px"
      :persistent="loading"
      scrollable
    >
      <ValidationObserver
        ref="generateKeysObserver"
        tag="form"
        @submit.prevent="onGenerateKeys"
      >
        <v-card>
          <dialog-title
            v-model="openGenerateKeys"
            title="Generate Keys"
            :disabled="loading"
          ></dialog-title>

          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                Use the form below to generate a set of
                <strong>Access Keys</strong>.
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  :rules="{
                    required: true,
                    numeric: true,
                    min_value: 1,
                    max_value: remainingAccessKeys,
                  }"
                  v-slot="{ errors }"
                  vid="Count"
                >
                  <v-text-field
                    v-model="generateKeys.count"
                    label="Count*"
                    hint="The number of Access Keys you want to generate."
                    persistent-hint
                    outlined
                    type="number"
                    step="1"
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col>
                <v-divider class="mb-3"></v-divider>
                <div class="d-flex align-center">
                  <div class="flex-fill">
                    <div class="text-h6">Meta Data</div>
                    <p class="text-body-2">
                      Tag the new access keys with identifying information.
                    </p>
                  </div>
                  <v-tooltip left v-if="showCounts">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        fab
                        color="primary"
                        small
                        elevation="0"
                        @click="
                          () => {
                            cancelAddMetaData();
                            addMetaData();
                          }
                        "
                        :disabled="addingMetaData || loading"
                        ><font-awesome-icon icon="plus"></font-awesome-icon
                      ></v-btn>
                    </template>
                    <span>Add Meta Data</span>
                  </v-tooltip>
                </div>

                <table class="stripped w-100">
                  <thead>
                    <td class="py-2 text-left grey darken-3 white--text">
                      Key
                    </td>
                    <td
                      class="py-2 text-left text-left grey darken-3 white--text"
                    >
                      Value
                    </td>
                    <td
                      class="
                        py-2
                        text-center text-left
                        grey
                        darken-3
                        white--text
                        w-0
                      "
                      v-if="showCounts"
                    >
                      Actions
                    </td>
                  </thead>
                  <template v-for="(item, index) in generateKeys.metaData">
                    <tr
                      :key="`read_${index}`"
                      v-if="
                        editingMetaData !== index && deletingMetaData !== index
                      "
                      @click="
                        () => {
                          if (editingMetaData == -1 && !addingMetaData) {
                            editMetaData(item, index);
                          }
                        }
                      "
                      :class="{
                        'hover pointer':
                          editingMetaData == -1 && !addingMetaData,
                        'grey--text': editingMetaData > -1 || addingMetaData,
                      }"
                    >
                      <td class="py-3 px-4">
                        {{ item.key }}
                      </td>
                      <td class="py-3 px-4">
                        {{ item.value }}
                      </td>
                      <td class="text-no-wrap py-3 px-4" v-if="showCounts">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              :disabled="
                                addingMetaData ||
                                  editingMetaData > -1 ||
                                  loading
                              "
                              @click.stop="editMetaData(item, index)"
                            >
                              <v-icon small>$edit</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              :disabled="
                                addingMetaData ||
                                  editingMetaData > -1 ||
                                  loading
                              "
                              @click.stop="deleteMetaData(item, index)"
                            >
                              <v-icon small>$delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete</span>
                        </v-tooltip>
                      </td>
                    </tr>
                    <ValidationObserver
                      v-slot="{ invalid }"
                      :key="`edit_${index}`"
                      v-if="editingMetaData === index && showCounts"
                      tag="tr"
                      ref="editMetaDataObserver"
                    >
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          rules="required|min:2|max:200"
                          v-slot="{ errors }"
                          vid="Key"
                        >
                          <v-text-field
                            label="Key*"
                            v-model="metaData.key"
                            outlined
                            dense
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            @keydown.enter.prevent="
                              confirmMetaData(generateKeys.metaData)
                            "
                            @keydown.esc="cancelMetaData"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          rules="required|min:2|max:200"
                          v-slot="{ errors }"
                          vid="value"
                        >
                          <v-text-field
                            label="Value*"
                            v-model="metaData.value"
                            outlined
                            dense
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            @keydown.enter.prevent="
                              confirmMetaData(generateKeys.metaData)
                            "
                            @keydown.esc="cancelMetaData"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="text-no-wrap px-4 py-4 text-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              outlined
                              @click="confirmMetaData(generateKeys.metaData)"
                              color="green darken-2"
                              :disabled="invalid || loading"
                              class="mr-2"
                            >
                              <v-icon small>$check</v-icon>
                            </v-btn>
                          </template>
                          <span>Confirm</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              color="red darken-2"
                              v-bind="attrs"
                              v-on="on"
                              icon
                              :disabled="loading"
                              outlined
                              @click="cancelMetaData"
                            >
                              <v-icon small>$close</v-icon>
                            </v-btn>
                          </template>
                          <span>Cancel</span>
                        </v-tooltip>
                      </td>
                    </ValidationObserver>
                    <tr
                      :key="`delete_${index}`"
                      v-if="deletingMetaData === index"
                    >
                      <td colspan="3" class="red lighten-4 red--text py-3">
                        <div class="d-flex align-center">
                          <div class="flex-fill">
                            You are about to delete
                            <strong>{{ item.key }}: {{ item.value }}</strong
                            >. Are you sure?
                          </div>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                @click="
                                  confirmDeleteMetaData(generateKeys.metaData)
                                "
                                color="red darken-2 white--text"
                                class="mr-2"
                              >
                                Yes, Delete
                              </v-btn>
                            </template>
                            <span>Confirm</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                @click="cancelDeleteMetaData"
                              >
                                Cancel
                              </v-btn>
                            </template>
                            <span>Cancel</span>
                          </v-tooltip>
                        </div>
                      </td>
                    </tr>
                  </template>
                  <ValidationObserver
                    v-if="
                      showCounts &&
                        (addingMetaData ||
                          (generateKeys.metaData &&
                            generateKeys.metaData.length === 0))
                    "
                    v-slot="{ invalid }"
                    tag="tr"
                    ref="addMetaDataObserver"
                  >
                    <td class="px-4 py-4" style="vertical-align: top">
                      <ValidationProvider
                        rules="required|min:2|max:200"
                        v-slot="{ errors }"
                        vid="addKey"
                      >
                        <v-text-field
                          label="Key*"
                          v-model="metaData.key"
                          outlined
                          dense
                          :error-messages="errors"
                          :hide-details="errors.length == 0"
                          @keydown.enter.prevent="
                            confirmAddMetaData(generateKeys.metaData)
                          "
                          @keydown.esc="cancelAddMetaData"
                        ></v-text-field>
                      </ValidationProvider>
                    </td>
                    <td class="px-4 py-4" style="vertical-align: top">
                      <ValidationProvider
                        rules="required|min:2|max:200"
                        v-slot="{ errors }"
                        vid="addValue"
                      >
                        <v-text-field
                          label="Value*"
                          v-model="metaData.value"
                          outlined
                          dense
                          :error-messages="errors"
                          :hide-details="errors.length == 0"
                          @keydown.enter.prevent="
                            confirmAddMetaData(generateKeys.metaData)
                          "
                          @keydown.esc="cancelAddMetaData"
                        ></v-text-field>
                      </ValidationProvider>
                    </td>
                    <td class="text-no-wrap px-4 py-4 text-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            outlined
                            @click="confirmAddMetaData(generateKeys.metaData)"
                            color="green darken-2"
                            :disabled="invalid || loading"
                            class="mr-2"
                          >
                            <v-icon small>$check</v-icon>
                          </v-btn>
                        </template>
                        <span>Confirm</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            color="red darken-2"
                            v-bind="attrs"
                            v-on="on"
                            icon
                            outlined
                            :disabled="loading"
                            v-if="
                              generateKeys.metaData &&
                                generateKeys.metaData.length > 0
                            "
                            @click="cancelAddMetaData"
                          >
                            <v-icon small>$close</v-icon>
                          </v-btn>
                        </template>
                        <span>Cancel</span>
                      </v-tooltip>
                    </td>
                  </ValidationObserver>
                </table>
              </v-col>
              <v-slide-y-transition>
                <v-col cols="12" v-if="existingBatch">
                  <v-divider class="mb-3"></v-divider>
                  <div class="text-h6">Existing Keys Found</div>
                  <p class="text-body-2 mb-8">
                    <strong>{{ existingBatch.total }} access key(s)</strong>
                    with the above meta data were found.
                  </p>
                  <ValidationProvider
                    rules="required"
                    v-slot="{ errors }"
                    vid="Workflow"
                  >
                    <v-select
                      v-model="generateKeys.workflow"
                      label="Which workflow would you like to use?*"
                      outlined
                      :items="workflowOptions"
                      :error-messages="errors"
                      :disabled="loading"
                    ></v-select>
                  </ValidationProvider>
                </v-col>
              </v-slide-y-transition>
              <v-slide-y-transition>
                <v-col
                  cols="12"
                  v-if="
                    existingBatch &&
                      generateKeys.workflow === workflows.replace.value
                  "
                >
                  <p class="text-body-2">
                    What do you want to do with replaced access keys?
                  </p>
                  <v-radio-group
                    v-model="generateKeys.existingCodeHandler"
                    column
                    :disabled="loading"
                  >
                    <v-radio
                      v-for="item in existingCodeHandlers"
                      :key="item.value"
                      :label="item.text"
                      :value="item.value"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
              </v-slide-y-transition>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <div class="d-flex align-center flex-fill px-2 py-1">
              <small class="flex-fill">*indicates required field</small>
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openGenerateKeys = false"
              >
                Cancel
              </v-btn>
              <v-btn :loading="loading" type="submit" color="primary">
                Generate
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <v-dialog
      v-model="openEditKey"
      max-width="700px"
      :persistent="loading"
      scrollable
    >
      <ValidationObserver
        ref="editKeyObserver"
        tag="form"
        @submit.prevent="onUpdateKey"
      >
        <v-card>
          <dialog-title
            v-model="openEditKey"
            title="Edit Key"
            :disabled="loading"
          ></dialog-title>

          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                Use the form below to edit an
                <strong>Access Key</strong>.
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editKey.code"
                  label="Code*"
                  outlined
                  disabled
                ></v-text-field>
              </v-col>
              <v-col>
                <v-divider class="mb-3"></v-divider>
                <div class="d-flex align-center">
                  <div class="flex-fill">
                    <div class="text-h6">Meta Data</div>
                    <p class="text-body-2">
                      Tag the access key with identifying information.
                    </p>
                  </div>
                  <v-tooltip left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        fab
                        color="primary"
                        small
                        elevation="0"
                        @click="
                          () => {
                            cancelAddMetaData();
                            addMetaData();
                          }
                        "
                        :disabled="addingMetaData || loading"
                        ><font-awesome-icon icon="plus"></font-awesome-icon
                      ></v-btn>
                    </template>
                    <span>Add Meta Data</span>
                  </v-tooltip>
                </div>

                <table class="stripped w-100">
                  <thead>
                    <td class="py-2 text-left grey darken-3 white--text">
                      Key
                    </td>
                    <td
                      class="py-2 text-left text-left grey darken-3 white--text"
                    >
                      Value
                    </td>
                    <td
                      class="
                        py-2
                        text-center text-left
                        grey
                        darken-3
                        white--text
                        w-0
                      "
                    >
                      Actions
                    </td>
                  </thead>
                  <template v-for="(item, index) in editKey.metaData">
                    <tr
                      :key="`read_${index}`"
                      v-if="
                        editingMetaData !== index && deletingMetaData !== index
                      "
                      @click="
                        () => {
                          if (editingMetaData == -1 && !addingMetaData) {
                            editMetaData(item, index);
                          }
                        }
                      "
                      :class="{
                        'hover pointer':
                          editingMetaData == -1 && !addingMetaData,
                        'grey--text': editingMetaData > -1 || addingMetaData,
                      }"
                    >
                      <td class="py-3 px-4">
                        {{ item.key }}
                      </td>
                      <td class="py-3 px-4">
                        {{ item.value }}
                      </td>
                      <td class="text-no-wrap py-3 px-4">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              :disabled="
                                addingMetaData ||
                                  editingMetaData > -1 ||
                                  loading
                              "
                              @click.stop="editMetaData(item, index)"
                            >
                              <v-icon small>$edit</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              :disabled="
                                addingMetaData ||
                                  editingMetaData > -1 ||
                                  loading
                              "
                              @click.stop="deleteMetaData(item, index)"
                            >
                              <v-icon small>$delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete</span>
                        </v-tooltip>
                      </td>
                    </tr>
                    <ValidationObserver
                      v-slot="{ invalid }"
                      :key="`edit_${index}`"
                      v-if="editingMetaData === index"
                      tag="tr"
                      ref="editMetaDataObserver"
                    >
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          rules="required|min:2|max:200"
                          v-slot="{ errors }"
                          vid="Key"
                        >
                          <v-text-field
                            label="Key*"
                            v-model="metaData.key"
                            outlined
                            dense
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            @keydown.enter.prevent="
                              confirmMetaData(editKey.metaData)
                            "
                            @keydown.esc="cancelMetaData"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          rules="required|min:2|max:200"
                          v-slot="{ errors }"
                          vid="value"
                        >
                          <v-text-field
                            label="Value*"
                            v-model="metaData.value"
                            outlined
                            dense
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            @keydown.enter.prevent="
                              confirmMetaData(editKey.metaData)
                            "
                            @keydown.esc="cancelMetaData"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="text-no-wrap px-4 py-4 text-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              outlined
                              @click="confirmMetaData(editKey.metaData)"
                              color="green darken-2"
                              :disabled="invalid || loading"
                              class="mr-2"
                            >
                              <v-icon small>$check</v-icon>
                            </v-btn>
                          </template>
                          <span>Confirm</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              color="red darken-2"
                              v-bind="attrs"
                              v-on="on"
                              icon
                              :disabled="loading"
                              outlined
                              @click="cancelMetaData"
                            >
                              <v-icon small>$close</v-icon>
                            </v-btn>
                          </template>
                          <span>Cancel</span>
                        </v-tooltip>
                      </td>
                    </ValidationObserver>
                    <tr
                      :key="`delete_${index}`"
                      v-if="deletingMetaData === index"
                    >
                      <td colspan="3" class="red lighten-4 red--text py-3">
                        <div class="d-flex align-center">
                          <div class="flex-fill">
                            You are about to delete
                            <strong>{{ item.key }}: {{ item.value }}</strong
                            >. Are you sure?
                          </div>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                @click="confirmDeleteMetaData(editKey.metaData)"
                                color="red darken-2 white--text"
                                class="mr-2"
                              >
                                Yes, Delete
                              </v-btn>
                            </template>
                            <span>Confirm</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                @click="cancelDeleteMetaData"
                              >
                                Cancel
                              </v-btn>
                            </template>
                            <span>Cancel</span>
                          </v-tooltip>
                        </div>
                      </td>
                    </tr>
                  </template>
                  <ValidationObserver
                    v-if="
                      addingMetaData ||
                        (editKey.metaData && editKey.metaData.length === 0)
                    "
                    v-slot="{ invalid }"
                    tag="tr"
                    ref="addMetaDataObserver"
                  >
                    <td class="px-4 py-4" style="vertical-align: top">
                      <ValidationProvider
                        rules="required|min:2|max:200"
                        v-slot="{ errors }"
                        vid="addKey"
                      >
                        <v-text-field
                          label="Key*"
                          v-model="metaData.key"
                          outlined
                          dense
                          :error-messages="errors"
                          :hide-details="errors.length == 0"
                          @keydown.enter.prevent="
                            confirmAddMetaData(editKey.metaData)
                          "
                          @keydown.esc="cancelAddMetaData"
                        ></v-text-field>
                      </ValidationProvider>
                    </td>
                    <td class="px-4 py-4" style="vertical-align: top">
                      <ValidationProvider
                        rules="required|min:2|max:200"
                        v-slot="{ errors }"
                        vid="addValue"
                      >
                        <v-text-field
                          label="Value*"
                          v-model="metaData.value"
                          outlined
                          dense
                          :error-messages="errors"
                          :hide-details="errors.length == 0"
                          @keydown.enter.prevent="
                            confirmAddMetaData(editKey.metaData)
                          "
                          @keydown.esc="cancelAddMetaData"
                        ></v-text-field>
                      </ValidationProvider>
                    </td>
                    <td class="text-no-wrap px-4 py-4 text-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            outlined
                            @click="confirmAddMetaData(editKey.metaData)"
                            color="green darken-2"
                            :disabled="invalid || loading"
                            class="mr-2"
                          >
                            <v-icon small>$check</v-icon>
                          </v-btn>
                        </template>
                        <span>Confirm</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            color="red darken-2"
                            v-bind="attrs"
                            v-on="on"
                            icon
                            outlined
                            :disabled="loading"
                            v-if="
                              editKey.metaData && editKey.metaData.length > 0
                            "
                            @click="cancelAddMetaData"
                          >
                            <v-icon small>$close</v-icon>
                          </v-btn>
                        </template>
                        <span>Cancel</span>
                      </v-tooltip>
                    </td>
                  </ValidationObserver>
                </table>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <div class="d-flex align-center flex-fill px-2 py-1">
              <small class="flex-fill">*indicates required field</small>
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openEditKey = false"
              >
                Cancel
              </v-btn>
              <v-btn :loading="loading" type="submit" color="primary">
                Save
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <v-dialog
      v-model="openGenerateBatches"
      max-width="700px"
      :persistent="loading"
      scrollable
    >
      <ValidationObserver
        ref="generateBatchesObserver"
        tag="form"
        @submit.prevent="onGenerateBatches"
      >
        <v-card>
          <dialog-title
            v-model="openGenerateBatches"
            title="Generate Keys from File"
            :disabled="loading"
          ></dialog-title>

          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                Use the form below to generate a set of
                <strong>Access Keys</strong> from a file.
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="generateBatches.filename"
                  accept=".csv"
                  :multiple="false"
                  label="Data File*"
                  outlined
                  :error-messages="fileErrors"
                  :disabled="loading"
                  @change="handleFileUpload"
                ></v-file-input>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  vid="Workflow"
                >
                  <v-select
                    v-model="generateBatches.workflow"
                    label="How would you like to handle existing keys?*"
                    outlined
                    :items="workflowOptions"
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-select>
                </ValidationProvider>
              </v-col>
              <v-slide-y-transition>
                <v-col
                  cols="12"
                  v-if="generateBatches.workflow === workflows.replace.value"
                >
                  <p class="text-body-2">
                    What do you want to do with replaced access keys?
                  </p>
                  <v-radio-group
                    v-model="generateBatches.existingCodeHandler"
                    column
                    :disabled="loading"
                  >
                    <v-radio
                      v-for="item in existingCodeHandlers"
                      :key="item.value"
                      :label="item.text"
                      :value="item.value"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
              </v-slide-y-transition>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <div class="d-flex align-center flex-fill px-2 py-1">
              <small class="flex-fill">*indicates required field</small>
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openGenerateBatches = false"
              >
                Cancel
              </v-btn>
              <v-btn :loading="loading" type="submit" color="primary">
                Generate
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <v-dialog
      v-model="openConfirmDelete"
      max-width="400px"
      :persistent="loading"
    >
      <v-card>
        <dialog-title
          v-model="openConfirmDelete"
          title="Confirm Delete"
          :disabled="loading"
        ></dialog-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">
                You are about to delete all
                <strong class="red--text darken-1 font-weight-bold">
                  Access Keys
                </strong>
                with the following meta data: <br />

                <v-chip
                  v-for="(data, i) in count.metaData"
                  :key="i"
                  class="mr-2 mt-2"
                  color="grey lighten-2"
                >
                  {{ data.key }}: {{ data.value }}
                </v-chip>
                <br />
                <br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openConfirmDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="deleteCodes"
              >
                Yes, delete keys
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="openConfirmDeleteAll"
      max-width="400px"
      :persistent="loading"
    >
      <v-card>
        <dialog-title
          v-model="openConfirmDeleteAll"
          title="Confirm Delete All"
          :disabled="loading"
        ></dialog-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">
                You are about to delete all
                <strong class="red--text darken-1 font-weight-bold">
                  Access Keys.
                </strong>
                <br />
                <br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openConfirmDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="deleteAllCodes"
              >
                Yes, delete all keys
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="openConfirmDeleteCode"
      max-width="400px"
      :persistent="loading"
    >
      <v-card>
        <dialog-title
          v-model="openConfirmDeleteCode"
          title="Delete Access Key"
          :disabled="loading"
        ></dialog-title>
        <v-card-text>
          <v-row v-if="keyIndex > -1 && batchItems[keyIndex]">
            <v-col cols="12">
              <p class="text-body-1">
                You are about to delete
                <strong class="red--text darken-1 font-weight-bold">
                  Access Keys: {{ batchItems[keyIndex].code }}
                </strong>
                with the following meta data: <br />

                <v-chip
                  v-for="(data, i) in batchItems[keyIndex].metaData"
                  :key="i"
                  class="mr-2 mt-2"
                  color="grey lighten-2"
                >
                  {{ data.key }}: {{ data.value }}
                </v-chip>
                <br />
                <br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openConfirmDeleteCode = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="deleteCode"
              >
                Yes, delete key
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="generateKeys.showConfirm"
      max-width="400px"
      :persistent="loading"
    >
      <v-card>
        <dialog-title
          v-model="generateKeys.showConfirm"
          title="Confirm Action"
          :disabled="loading"
        ></dialog-title>
        <v-card-text>
          <v-row v-if="existingBatch">
            <v-col cols="12">
              <p class="text-body-1">
                You are about to {{ generateKeys.existingCodeHandler }}
                <strong class="red--text darken-1 font-weight-bold">
                  {{ existingBatch.total }} Access Keys
                </strong>
                with the following meta data: <br />

                <v-chip
                  v-for="(data, i) in existingBatch.metaData"
                  :key="i"
                  class="mr-2 mt-2"
                  color="grey lighten-2"
                >
                  {{ data.key }}: {{ data.value }}
                </v-chip>
                <br />
                <br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="generateKeys.showConfirm = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="
                  () => {
                    generateKeys.confirmedReplace = true;
                    onGenerateKeys();
                  }
                "
              >
                Yes, {{ generateKeys.existingCodeHandler }} keys
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="generateBatches.showConfirm"
      max-width="400px"
      :persistent="loading"
    >
      <v-card>
        <dialog-title
          v-model="generateBatches.showConfirm"
          title="Confirm Action"
          :disabled="loading"
        ></dialog-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">
                You are about to
                {{ generateBatches.existingCodeHandler }} existing
                <strong class="red--text darken-1 font-weight-bold"
                  >Access Keys</strong
                >
                with meta data found in your file.
                <br />
                <br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="generateBatches.showConfirm = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="
                  () => {
                    generateBatches.confirmedReplace = true;
                    onGenerateBatches();
                  }
                "
              >
                Yes, {{ generateBatches.existingCodeHandler }} keys
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="openConfirmKeySettings" max-width="400px">
      <v-card>
        <dialog-title v-model="openConfirmKeySettings" :disabled="loading">
          <template v-slot:title>
            <div class="text-h5 flex-fill align-center d-flex">
              <v-icon class="text-h5 red--text darken-text-2 mr-2"
                >$warning</v-icon
              >
              Confirm Changes
            </div>
          </template>
        </dialog-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">
                You are about to change your survey's
                <strong class="red--text darken-1 font-weight-bold">
                  Access Key Settings.
                </strong>
                This change will permanently delete all access keys, and cannot
                be undone.

                <br />
                <br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openConfirmKeySettings = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="
                  () => {
                    openConfirmKeySettings = false;
                    updateKeySettings();
                  }
                "
              >
                Yes, change settings
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from "vue";
import numeral from "numeral";
import { parse } from "json2csv";

import { exportCSVFile } from "../../utilities/dataUtilities";
import surveyService from "../../services/surveyService";
import DialogTitle from "../../components/DialogTitle.vue";
import { factorialize } from "../../utilities/dataUtilities";

import { surveyStatus, accessKeyUseTypes } from "../../constants/Survey";

const metaDataPrefix = "Meta Data:";

const newCount = () => ({
  metaData: [],
  count: 0,
});

const workflows = {
  append: {
    text: "Append new keys to existing sets",
    value: "append",
  },
  replace: {
    text: "Replace existing keys",
    value: "replace",
  },
};

const existingCodeHandlers = {
  deactivate: {
    text: "Deactivate",
    value: "deactivate",
  },
  delete: {
    text: "Delete",
    value: "delete",
  },
};

const generateKeysTemplate = () => ({
  count: 1,
  metaData: [],
  workflow: workflows.append.value,
  existingCodeHandler: existingCodeHandlers.deactivate.value,
  showConfirm: false,
  confirmedReplace: false,
});

const generateBatchesTemplate = () => ({
  fileName: null,
  workflow: workflows.append.value,
  existingCodeHandler: existingCodeHandlers.deactivate.value,
  showConfirm: false,
  confirmedReplace: false,
});

const newMetaData = () => ({
  key: "",
  value: "",
});

export default {
  components: {
    DialogTitle,
  },
  data() {
    return {
      dbSurvey: {},
      survey: {},
      codeStats: {
        total: 0,
        used: 0,
        rate: 0
      },
      search: "",
      batchSearch: "",
      counts: [],
      batch: [],
      count: newCount(),
      batchItems: [],
      openConfirmDelete: false,
      openConfirmDeleteAll: false,
      pendingStateChanges: 0,
      metaDataKeys: [],
      showCounts: true,
      openAccessKeySettings: false,
      openConfirmKeySettings: false,
      openGenerateBatches: false,
      openGenerateKeys: false,
      openEditKey: false,
      generateKeys: generateKeysTemplate(),
      generateBatches: generateBatchesTemplate(),
      editKey: {},
      addingMetaData: false,
      newMetaData: newMetaData(),
      editingMetaData: -1,
      metaData: newMetaData(),
      deletingMetaData: -1,
      workflows,
      existingCodeHandlers,
      openConfirmDeleteCode: false,
      keyIndex: -1,
      fileErrors: [],
    };
  },
  computed: {
    headers() {
      let results = [];

      for (let i = 0; i < this.counts.length; i++) {
        for (let key in this.counts[i]) {
          if (
            !results.some((x) => x.value === key) &&
            !["id", "total", "metaData"].some((x) => x === key)
          ) {
            results.push({
              text: `${metaDataPrefix} ${key}`,
              value: key,
              sortable: true,
            });
          }
        }
      }

      let actions = {
        text: "Actions",
        value: "actions",
        sortable: false,
        cellClass: "w-0 text-no-wrap",
        align: "center",
      };

      let metaData = {
        text: "MetaData",
        value: "metaData",
        sortable: false,
        cellClass: "d-none",
        class: "d-none",
        align: "center",
      };

      // let active = {
      //   text: "Active",
      //   value: "active",
      //   align: "center",
      // };

      //let used = {
      // text: "Used",
      // value: "used",
      // align: "center",
      // };

      let total = {
        text: "Total",
        value: "total",
        align: "right",
        class: "text-right",
        cellClass: "text-right",
      };

      return results.concat([metaData, total, actions]);
    },
    batchHeaders() {
      return [
        {
          text: "Access Key",
          value: "code",
        },
        {
          text: "Used",
          value: "used",
          align: "center",
        },
        {
          text: "Active",
          value: "active",
          align: "center",
        },
        {
          text: "Created",
          value: "created",
          align: "right",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          cellClass: "w-0 text-no-wrap",
          align: "center",
        },
      ];
    },
    breadcrumbs() {
      return [
        {
          text: "All Surveys",
          exact: true,
          to: { name: "surveys" },
        },
        {
          text: this.loading
            ? "Loading..."
            : this.survey.name ?? "Unnamed Survey",
          disable: true,
        },
      ];
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    tenantId() {
      return this.$store.state.tenantId;
    },
    updatedKeySettings() {
      return (
        this.survey.accessKeyChars !== this.dbSurvey.accessKeyChars ||
        this.survey.accessKeyLength !== this.dbSurvey.accessKeyLength
      );
    },
    accessKeyChars: {
      get() {
        return this.survey.accessKeyChars;
      },
      set(val) {
        Vue.set(this.survey, "accessKeyChars", val.toUpperCase());
      },
    },
    possibleAccessKeys() {
      let n = this.survey.accessKeyChars?.length ?? 0;
      let r = this.survey.accessKeyLength;
      if (!n || !r) return 0;
      return numeral(factorialize(n) / factorialize(n - r)).format("0,0");
    },
    remainingAccessKeys() {
      return numeral(this.possibleAccessKeys).value() - this.counts.length;
    },
    existingBatch() {
      for (let i = 0; i < this.counts.length; i++) {
        let batch = this.counts[i];
        let sorter = (a, b) => (a.key > b.key ? 1 : -1);
        let a = JSON.stringify(batch.metaData.slice().sort(sorter));
        let b = JSON.stringify(this.generateKeys.metaData.slice().sort(sorter));
        if (a === b) return JSON.parse(JSON.stringify(batch));
      }

      return null;
    },
    workflowOptions() {
      let results = [];

      for (var key in workflows) {
        results.push(workflows[key]);
      }

      return results;
    },
    workflow() {
      return this.workflowOptions.find(
        (x) => x.value === this.generateKeys.workflow
      );
    },
    archived() {
      return this.survey.status === surveyStatus.archived.key;
    },
    accessKeyUseTypes() {
      let results = [];
      for (const key in accessKeyUseTypes) {
        let val = accessKeyUseTypes[key];
        results.push({
          text: val.charAt(0).toUpperCase() + val.slice(1),
          value: val,
        });
      }
      return results;
    },
  },
  watch: {
    ["$route.params.id"](val, prev) {
      if (val != prev) {
        this.counts = [];
        this.load();
      }
    },
    openAccessKeySettings() {
      let data = JSON.parse(JSON.stringify(this.dbSurvey));

      this.accessKeyChars = this.dbSurvey.accessKeyChars
        ? data.accessKeyChars
        : "";

      this.survey.accessKeyLength = this.survey.accessKeyLength
        ? data.accessKeyLength
        : "";

      this.survey.accessKeyUseType = this.survey.accessKeyUseType
        ? data.accessKeyUseType
        : null;

      if (this.$refs.accessKeySettingsObserver)
        this.$refs.accessKeySettingsObserver.reset();
    },
    openGenerateKeys() {
      this.generateKeys = generateKeysTemplate();
      this.generateKeys.metaData = !this.showCounts
        ? JSON.parse(JSON.stringify(this.count.metaData))
        : [];

      this.addingMetaData = false;
      this.metaDataRow = newMetaData();
      this.editingMetaData = -1;

      this.metaData = newMetaData();
      this.deletingMetaData = -1;

      if (this.$refs.generateKeysObserver)
        this.$refs.generateKeysObserver.reset();
    },
    openGenerateBatches() {
      this.file = null;
      this.fileErrors = [];
      this.generateBatches = generateBatchesTemplate();

      if (this.$refs.generateBatchesObserver)
        this.$refs.generateBatchesObserver.reset();
    },
    openEditKey() {
      this.addingMetaData = false;
      this.metaDataRow = newMetaData();
      this.editingMetaData = -1;

      this.metaData = newMetaData();
      this.deletingMetaData = -1;

      if (this.$refs.editKeyObserver) this.$refs.editKeyObserver.reset();
    },
    existingBatch(val, prev) {
      if (val && val != prev) {
        this.generateKeys.workflow = workflows.append.value;
        this.generateKeys.existingCodeHandler =
          existingCodeHandlers.deactivate.value;
      }
    },
  },
  methods: {
    filter(value, search) {
      return (
        value != null &&
        search != null &&
        typeof value === "string" &&
        value
          .toString()
          .toLowerCase()
          .indexOf(search.toLowerCase()) !== -1
      );
    },
    load() {
      this.pendingStateChanges++;

      let promises = [];

      // Get survey detail
      promises.push(surveyService.get(this.tenantId, this.$route.params.id));

      // load tables
      promises.push(
        surveyService.getCodeCounts(this.tenantId, this.$route.params.id),
        surveyService.getCodeStats(this.tenantId, this.$route.params.id)
      );

      Promise.all(promises)
        .then(
          (results) => {
            this.survey = results[0];
            this.dbSurvey = JSON.parse(JSON.stringify(this.survey));

            let keys = [];

            this.counts = results[1]
              .map((x) => {
                for (let i = 0; i < x.metaData.length; i++) {
                  let key = x.metaData[i].key;
                  x[key] = x.metaData[i].value;

                  if (!keys.some((x) => x === key)) keys.push(key);
                }

                return x;
              })
              .map((x) => {
                keys.forEach((key) => {
                  if (!x[key]) x[key] = "--";
                });

                return x;
              });

            this.codeStats = results[2].codeStats.reduce(
              (agg, stat, i) => {
                agg.total += stat.total;
                agg.used += stat.used;
                if (i === results[2].codeStats.length - 1) {
                  let rate = 0;
                  if (agg.total && agg.used) {
                    rate = agg.used / agg.total;
                  }

                  agg.rate = numeral(rate).format("0.00%");
                }

                return agg;
              },
              {
                total: 0,
                used: 0,
                rate: 0,
              }
            );
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while loading access keys. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    loadBatch() {
      this.pendingStateChanges++;

      this.batchItems = [];

      surveyService
        .getCodes(this.tenantId, this.$route.params.id, this.count.metaData)
        .then(
          (results) => {
            this.batchItems = results.map((x) => {
              return x;
            });
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while loading access keys. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    confirmDeleteBatch(count) {
      if (this.archived) return;
      this.keyIndex = this.counts.findIndex(
        (x) => JSON.stringify(count.metaData) === JSON.stringify(x.metaData)
      );

      this.count = count;
      this.openConfirmDelete = true;
    },
    showBatch(e, { item }) {
      this.count = item;
      this.showCounts = false;
      this.loadBatch();
    },
    async onUpdateKeySettings() {
      const isValid = await this.$refs.accessKeySettingsObserver.validate();
      if (!isValid || this.loading || !this.updatedKeySettings || this.archived)
        return;

      if (this.counts.length > 0) {
        this.openConfirmKeySettings = true;
      } else this.updateKeySettings();
    },
    async updateKeySettings() {
      if (this.archived) return;

      this.pendingStateChanges++;

      surveyService
        .updateAccessKeySettings(this.tenantId, this.survey.id, this.survey)
        .then(
          () => {
            let data = JSON.parse(JSON.stringify(this.survey));
            this.dbSurvey.accessKeyChars = data.accessKeyChars;
            this.dbSurvey.accessKeyLength = data.accessKeyLength;
            this.openAccessKeySettings = false;

            this.$store.dispatch(
              "notifications/success",
              `Successfully updated access key settings.`
            );
          },
          (errorResponse) => {
            if (errorResponse.statusCode === 400) {
              this.$refs.accessKeySettingsObserver.setErrors(
                errorResponse.result
              );
            } else {
              this.$store.dispatch(
                "notifications/error",
                "An unexpected error occurred while trying to update access key settings. Please try again later."
              );
            }
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    showKey(item) {
      this.openEditKey = true;
      this.editKey = JSON.parse(JSON.stringify(item));
    },
    editMetaData(data, index) {
      this.metaData = JSON.parse(JSON.stringify(data));
      this.editingMetaData = index;
    },
    cancelMetaData() {
      this.metaData = newMetaData();
      this.editingMetaData = -1;
    },
    confirmMetaData(source) {
      this.$refs.editMetaDataObserver[0].validate().then((isValid) => {
        if (isValid) {
          Vue.set(source, this.editingMetaData, this.metaData);
          this.cancelMetaData();
        }
      });
    },
    addMetaData() {
      this.metaData = newMetaData();
      this.addingMetaData = true;
    },
    cancelAddMetaData() {
      this.metaData = newMetaData();
      this.addingMetaData = false;
    },
    confirmAddMetaData(source) {
      this.$refs.addMetaDataObserver.validate().then((isValid) => {
        if (isValid) {
          source.push(this.metaData);
          this.cancelAddMetaData();
        }
      });
    },
    deleteMetaData(item, index) {
      this.deletingMetaData = index;
    },
    cancelDeleteMetaData() {
      this.deletingMetaData = -1;
    },
    confirmDeleteMetaData(source) {
      source.splice(this.deletingMetaData, 1);
      this.metaData = newMetaData();
      this.cancelDeleteMetaData();
    },
    async onGenerateKeys() {
      const isValid = await this.$refs.generateKeysObserver.validate();
      if (!isValid || this.loading || this.archived) return;

      if (
        this.existingBatch &&
        this.generateKeys.workflow == workflows.replace.value &&
        !this.generateKeys.confirmedReplace
      ) {
        this.generateKeys.showConfirm = true;
        return;
      } else {
        this.generateKeys.showConfirm = false;
      }

      this.pendingStateChanges++;

      surveyService
        .generateCodes(
          this.tenantId,
          this.survey.id,
          [
            {
              total: this.generateKeys.count,
              metaData: this.generateKeys.metaData,
            },
          ],
          this.generateKeys.workflow,
          this.generateKeys.existingCodeHandler
        )
        .then(
          () => {
            this.load();
            if (!this.showCounts) this.loadBatch();
            this.openGenerateKeys = false;
            this.$store.dispatch(
              "notifications/success",
              `Successfully generated new access keys.`
            );
          },
          (errorResponse) => {
            if (errorResponse.statusCode === 400) {
              this.$refs.generateKeysObserver.setErrors(errorResponse.result);
            } else {
              this.$store.dispatch(
                "notifications/error",
                "An unexpected error occurred while trying to generate access keys. Please try again later."
              );
            }
          }
        )
        .then(() => this.pendingStateChanges--);
    },
    async onGenerateBatches() {
      const isValid = await this.$refs.generateBatchesObserver.validate();
      if (!isValid || this.loading || this.archived) return;

      if (!this.file) {
        this.fileErrors = ["File is required"];
        return;
      }

      if (
        this.generateBatches.workflow === workflows.replace &&
        !this.generateBatches.confirmedReplace
      ) {
        this.generateBatches.showConfirm = true;
        return;
      } else {
        this.generateBatches.showConfirm = false;
      }

      this.pendingStateChanges++;

      surveyService
        .generateCodeSets(
          this.tenantId,
          this.survey.id,
          this.file,
          this.generateBatches.workflow,
          this.generateBatches.existingCodeHandler
        )
        .then(
          () => {
            this.load();
            this.file = null;
            this.openGenerateBatches = false;
            this.$store.dispatch(
              "notifications/success",
              `Successfully generated new access keys.`
            );
          },
          (errorResponse) => {
            if (errorResponse.statusCode === 400) {
              this.$refs.generateBatchesObserver.setErrors(
                errorResponse.result
              );
            } else {
              this.$store.dispatch(
                "notifications/error",
                "An unexpected error occurred while trying to generate access keys. Please try again later."
              );
            }
          }
        )
        .then(() => this.pendingStateChanges--);
    },
    async onUpdateKey() {
      const isValid = await this.$refs.editKeyObserver.validate();
      if (!isValid || this.loading || this.archived) return;

      this.pendingStateChanges++;

      surveyService
        .patchCode(this.tenantId, this.survey.id, {
          id: this.editKey.id,
          metaData: this.editKey.metaData,
        })
        .then(
          () => {
            this.loadBatch();
            this.load();
            this.openEditKey = false;
            this.$store.dispatch(
              "notifications/success",
              `Successfully updated access key.`
            );
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to update access key. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    toggleActiveStatus(item) {
      this.pendingStateChanges++;

      var data = JSON.parse(JSON.stringify(item));
      data.active = !data.active;

      surveyService
        .patchCode(this.tenantId, this.survey.id, data)
        .then(
          () => {
            item.active = !item.active;
            this.$store.dispatch(
              "notifications/success",
              `Successfully updated access key.`
            );
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to update access key. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    confirmDeleteCode(item) {
      if (this.archived) return;
      this.keyIndex = this.batchItems.findIndex((x) => x.id === item.id);
      this.openConfirmDeleteCode = true;
    },
    deleteCode() {
      if (this.archived) return;
      this.pendingStateChanges++;

      surveyService
        .deleteCode(
          this.tenantId,
          this.survey.id,
          this.batchItems[this.keyIndex]
        )
        .then(
          () => {
            this.batchItems.splice(this.keyIndex, 1);
            this.keyIndex = -1;
            this.count.total--;
            this.openConfirmDeleteCode = false;
            this.$store.dispatch(
              "notifications/success",
              `Successfully deleted access key.`
            );
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to deleting access key. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    deleteCodes() {
      if (this.archived) return;
      this.pendingStateChanges++;

      surveyService
        .deleteCodes(
          this.tenantId,
          this.survey.id,
          this.counts[this.keyIndex].metaData
        )
        .then(
          () => {
            this.counts.splice(this.keyIndex, 1);
            this.keyIndex = -1;
            this.openConfirmDelete = false;
            this.$store.dispatch(
              "notifications/success",
              `Successfully deleted access keys.`
            );
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to deleting access keys. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    deleteAllCodes() {
      if (this.archived) return;
      this.pendingStateChanges++;

      surveyService
        .deleteCodes(this.tenantId, this.survey.id, [])
        .then(
          () => {
            this.counts = [];
            this.keyIndex = -1;
            this.openConfirmDeleteAll = false;
            this.$store.dispatch(
              "notifications/success",
              `Successfully deleted access keys.`
            );
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to deleting access keys. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    downloadBatch() {
      try {
        var fileName = `${this.survey.id}_AccessKeys`;

        let fields = [
          {
            label: "Access Key",
            value: "key",
          },
          {
            label: "Active",
            value: "active",
          },
        ];

        this.headers
          .filter((x) => x.text.indexOf(metaDataPrefix) === 0)
          .forEach((x) => {
            fields.push({
              label: x.text,
              value: x.value,
            });
          });

        let data = this.batchItems.slice().map((x) => {
          let result = {
            key: x.code,
            active: x.active,
          };

          x.metaData.forEach((y) => {
            result[y.key] = y.value;
          });

          return result;
        });

        const csv = parse(data, { fields });
        exportCSVFile(csv, fileName);
      } catch (err) {
        console.error(err);
      }
    },
    downloadCounts() {
      try {
        var fileName = `${this.survey.id}_MetaDataCounts`;

        let fields = [
          {
            label: "Total",
            value: "total",
          },
        ];

        this.headers
          .filter((x) => x.text.indexOf(metaDataPrefix) === 0)
          .forEach((x) => {
            fields.push({
              label: x.text,
              value: x.value,
            });
          });

        let data = this.counts.slice().map((x) => {
          let result = {
            total: x.total,
          };

          x.metaData.forEach((y) => {
            result[y.key] = y.value;
          });

          return result;
        });

        const csv = parse(data, { fields });
        exportCSVFile(csv, fileName);
      } catch (err) {
        console.error(err);
      }
    },
    downloadCodes(filters) {
      this.pendingStateChanges++;
      return surveyService
        .downloadCodes(this.tenantId, this.survey.id, filters)
        .finally(() => this.pendingStateChanges--);
    },
    downloadResponses(filters) {
      this.pendingStateChanges++;
      return surveyService
        .downloadResponses(this.tenantId, this.survey.id, filters)
        .finally(() => this.pendingStateChanges--);
    },
    handleFileUpload(file) {
      this.file = file;
      if (!this.file) this.fileErrors = ["File is required"];
      else this.fileErrors = [];
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style>
.w-0 {
  width: 0;
}
</style>
